import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldCheck, CheckCircle, XCircle, Clock, Building2, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== VERIFICATIONS TAB ====================
function VerificationsTab({ language }) {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [reviewingId, setReviewingId] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [processing, setProcessing] = useState(null); // {id, action} during approve/reject

  useEffect(() => { fetchRequests(); }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/verifications`, { params: { status: filter || undefined } });
      setRequests(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleReview = async (id, action) => {
    setProcessing({ id, action });
    try {
      await axios.put(`${API}/admin/verifications/${id}`, { action, admin_note: adminNote });
      setReviewingId(null);
      setAdminNote('');
      fetchRequests();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Error');
    } finally {
      setProcessing(null);
    }
  };

  const STATUS_STYLES = {
    pending: { bg: 'bg-amber-50 text-amber-700', icon: Clock, lv: 'Gaida', en: 'Pending' },
    approved: { bg: 'bg-green-50 text-green-700', icon: CheckCircle, lv: 'Apstiprināts', en: 'Approved' },
    rejected: { bg: 'bg-red-50 text-red-700', icon: XCircle, lv: 'Noraidīts', en: 'Rejected' },
  };

  const ENTITY_LABELS = {
    sia: 'SIA', ik: 'IK', as: 'AS', individual: language === 'lv' ? 'Fiziska persona' : 'Individual',
    biedriba: language === 'lv' ? 'Biedrība' : 'Association',
    pasvaldiba: language === 'lv' ? 'Pašvaldība' : 'Municipality',
  };

  // Count completeness signals so the admin can see at a glance whether a request is reviewable.
  const completeness = (req) => {
    const sp = req.shop_profile || {};
    const checks = [
      { label: language === 'lv' ? 'Veikala nosaukums' : 'Shop name', ok: !!sp.shop_name },
      { label: language === 'lv' ? 'Apraksts' : 'Description', ok: !!sp.shop_description },
      { label: language === 'lv' ? 'Tel.' : 'Phone', ok: !!sp.shop_phone },
      { label: language === 'lv' ? 'Uzņēmuma tips' : 'Entity type', ok: !!sp.entity_type },
      {
        label: language === 'lv' ? 'Juridiskie dati' : 'Legal info',
        ok: sp.entity_type === 'individual'
          ? !!sp.legal_address
          : !!(sp.legal_name && sp.registration_number && sp.legal_address),
      },
      { label: language === 'lv' ? 'Dokumenti' : 'Documents', ok: (req.document_count || 0) > 0 },
    ];
    const done = checks.filter((c) => c.ok).length;
    return { checks, done, total: checks.length };
  };

  return (
    <div data-testid="admin-verifications">
      <div className="flex gap-2 mb-6 flex-wrap">
        {['pending', 'approved', 'rejected', ''].map((s) => {
          const count = s === filter ? requests.length : null;
          return (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === s ? 'bg-[#121212] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#121212]'
              }`}
              data-testid={`filter-${s || 'all'}`}
            >
              {s === '' ? (language === 'lv' ? 'Visi' : 'All')
                : s === 'pending' ? (language === 'lv' ? 'Gaida' : 'Pending')
                : s === 'approved' ? (language === 'lv' ? 'Apstiprināti' : 'Approved')
                : (language === 'lv' ? 'Noraidīti' : 'Rejected')}
              {count !== null && <span className="ml-2 opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" />)}</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-[#5C5C5C]">
          <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>{language === 'lv' ? 'Nav pieteikumu' : 'No requests'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => {
            const style = STATUS_STYLES[req.status] || STATUS_STYLES.pending;
            const StatusIcon = style.icon;
            const sp = req.shop_profile || {};
            const isExpanded = expandedId === req.id;
            const comp = completeness(req);
            const isProcessing = processing?.id === req.id;
            return (
              <div key={req.id} className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden" data-testid={`verification-${req.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-[#121212] text-lg" data-testid={`ver-shop-name-${req.id}`}>
                          {sp.shop_name || (language === 'lv' ? '(Nav veikala profila)' : '(No shop profile)')}
                        </h3>
                        <Badge className={`${style.bg} border-0`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {language === 'lv' ? style.lv : style.en}
                        </Badge>
                        {sp.entity_type && <Badge variant="outline">{ENTITY_LABELS[sp.entity_type] || sp.entity_type}</Badge>}
                        {req.status === 'approved' && (
                          req.verification_method === 'ur_registry' ? (
                            <Badge className="bg-green-100 text-green-800 border-0" data-testid={`ver-method-auto-${req.id}`}>
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              {language === 'lv' ? 'Auto · UR reģistrs' : 'Auto · Registry'}
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 border-0" data-testid={`ver-method-manual-${req.id}`}>
                              {language === 'lv' ? 'Manuāli' : 'Manual'}
                            </Badge>
                          )
                        )}
                      </div>
                      <div className="text-sm text-[#5C5C5C] space-y-0.5">
                        <p><strong className="text-[#121212]">{req.user_name}</strong> — {req.user_email}</p>
                        {req.user_phone && <p>📞 {req.user_phone}</p>}
                        <p className="text-xs">
                          {language === 'lv' ? 'Iesniegts:' : 'Submitted:'} {new Date(req.created_at).toLocaleString(language === 'lv' ? 'lv-LV' : 'en-US')}
                        </p>
                      </div>

                      {/* Completeness indicator */}
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1">
                          <div className="w-32 h-2 bg-[#F0EFEB] rounded-full overflow-hidden">
                            <div
                              className={`h-full ${comp.done === comp.total ? 'bg-green-600' : 'bg-amber-500'}`}
                              style={{ width: `${(comp.done / comp.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-[#5C5C5C] ml-1">
                            {comp.done}/{comp.total} {language === 'lv' ? 'aizpildīti' : 'filled'}
                          </span>
                        </div>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : req.id)}
                          className="text-xs text-[#D97736] hover:underline font-medium"
                          data-testid={`toggle-details-${req.id}`}
                        >
                          {isExpanded
                            ? (language === 'lv' ? 'Slēpt detaļas' : 'Hide details')
                            : (language === 'lv' ? 'Skatīt detaļas' : 'Show details')}
                        </button>
                      </div>

                      {req.admin_note && (
                        <div className="mt-3 p-2 bg-[#F3F2EE] rounded-lg text-sm text-[#5C5C5C]">
                          <strong>{language === 'lv' ? 'Admin piezīme:' : 'Admin note:'}</strong> {req.admin_note}
                        </div>
                      )}
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleReview(req.id, 'approve')}
                          disabled={isProcessing}
                          data-testid={`approve-${req.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {language === 'lv' ? 'Apstiprināt' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          onClick={() => setReviewingId(req.id)}
                          disabled={isProcessing}
                          data-testid={`reject-btn-${req.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          {language === 'lv' ? 'Noraidīt' : 'Reject'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-5 pt-5 border-t border-[#F0EFEB] grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={`details-${req.id}`}>
                      {/* Shop profile fields */}
                      <div>
                        <h4 className="font-semibold text-[#121212] text-sm mb-2 flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {language === 'lv' ? 'Veikala dati' : 'Shop data'}
                        </h4>
                        <div className="space-y-1 text-sm">
                          {sp.shop_description && (
                            <div className="mb-2 text-[#5C5C5C]">{sp.shop_description}</div>
                          )}
                          {[
                            ['legal_name', language === 'lv' ? 'Juridiskais nosaukums' : 'Legal name'],
                            ['registration_number', language === 'lv' ? 'Reģ. numurs' : 'Reg. number'],
                            ['vat_number', language === 'lv' ? 'PVN numurs' : 'VAT number'],
                            ['legal_address', language === 'lv' ? 'Juridiskā adrese' : 'Legal address'],
                            ['shop_phone', language === 'lv' ? 'Veikala tālrunis' : 'Shop phone'],
                            ['bank_name', language === 'lv' ? 'Banka' : 'Bank'],
                            ['bank_account', 'IBAN'],
                          ].map(([key, label]) => sp[key] && (
                            <div key={key} className="flex justify-between gap-3">
                              <span className="text-[#8A8A8A]">{label}:</span>
                              <span className="text-[#121212] font-medium text-right">{sp[key]}</span>
                            </div>
                          ))}
                        </div>

                        {/* Completeness checklist */}
                        <div className="mt-4">
                          <h5 className="text-xs font-semibold text-[#5C5C5C] uppercase mb-2">
                            {language === 'lv' ? 'Pārbaudes saraksts' : 'Checklist'}
                          </h5>
                          <ul className="space-y-1 text-sm">
                            {comp.checks.map((c, i) => (
                              <li key={i} className="flex items-center gap-2">
                                {c.ok ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-400" />}
                                <span className={c.ok ? 'text-[#121212]' : 'text-[#8A8A8A]'}>{c.label}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Documents */}
                      <div>
                        <h4 className="font-semibold text-[#121212] text-sm mb-2 flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {language === 'lv' ? 'Dokumenti' : 'Documents'} ({req.document_count || 0})
                        </h4>
                        {(!req.documents || req.documents.length === 0) ? (
                          <p className="text-sm text-[#8A8A8A]">
                            {language === 'lv' ? 'Lietotājs nav augšupielādējis dokumentus.' : 'No documents uploaded.'}
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {req.documents.map((doc) => (
                              <li key={doc.id} className="flex items-start gap-2 p-2 rounded-lg border border-[#F0EFEB] hover:bg-[#FAFAFA]">
                                <FileText className="w-4 h-4 mt-0.5 text-[#D97736] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-[#121212] truncate">
                                    {doc.custom_type_name || doc.type || (language === 'lv' ? 'Dokuments' : 'Document')}
                                  </div>
                                  <div className="text-xs text-[#8A8A8A] truncate">{doc.filename}</div>
                                  {doc.url && (
                                    <a
                                      href={doc.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-[#D97736] hover:underline"
                                      data-testid={`doc-link-${doc.id}`}
                                    >
                                      {language === 'lv' ? 'Atvērt →' : 'Open →'}
                                    </a>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
                          <strong>{language === 'lv' ? 'Padoms:' : 'Tip:'}</strong>{' '}
                          {language === 'lv'
                            ? 'Pirms apstiprināšanas pārbaudi, vai dokumenti ir derīgi un atbilst veikala datiem.'
                            : 'Verify the documents are valid and match the shop data before approving.'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!reviewingId} onOpenChange={() => { setReviewingId(null); setAdminNote(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'lv' ? 'Noraidīt pieteikumu' : 'Reject Request'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C5C5C]">
              {language === 'lv'
                ? 'Lūdzu, norādi noraidīšanas iemeslu. Lietotājs saņems šo paziņojumu e-pastā un platformā.'
                : 'Please provide the rejection reason. The user will receive this note via email and in-app.'}
            </p>
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder={language === 'lv' ? 'piem., dokumenti nesakrīt ar veikala datiem' : 'e.g., documents do not match shop data'}
              rows={3}
              data-testid="reject-note-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setReviewingId(null); setAdminNote(''); }}>
                {language === 'lv' ? 'Atcelt' : 'Cancel'}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => handleReview(reviewingId, 'reject')}
                data-testid="confirm-reject-btn"
              >
                {language === 'lv' ? 'Noraidīt' : 'Reject'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VerificationsTab;
